import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Row, Card, Col, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { RUTA_PROPIETARIOS } from "../../../services/rutas";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import {
  buscarPropietario,
  buscarContrato,
  RespuestaBuscarPropietario as Propietario,
  Archivo,
  prettyTipoDocumento,
  prettyCanalMarketing,
  useListarAgendamientosActivos,
} from "../../../services/propietariosServicios";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import { downloadBlob } from "../../../utils/downloadUtils";
import { convertBase64ToBlob } from "../../../utils/blobUtils";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { TabsSection } from "./TabsSection";
import { AgendamientoResumido } from "../../../services/agendamientosServicios";
import { AgendamientoModal } from "./AgendamientoModal";

function CardSection() {
  let navigate = useNavigate();
  let { idPropietario } = useParams();
  const [propietario, setPropietario] = useState({} as Propietario);
  const [serverErrorOcurred, setServerErrorOcurred] = useState(false);
  const [agendamientoSeleccionado, setAgendamientoSeleccionado] = useState<
    AgendamientoResumido | undefined
  >();
  const { data: agendamientosActivos = [], isError: agendamientosIsError } =
    useListarAgendamientosActivos(idPropietario);

  const onDescargarContratoClick = async () => {
    setServerErrorOcurred(false);
    if (idPropietario) {
      let contrato: Archivo | undefined | null;
      try {
        contrato = await buscarContrato(idPropietario);
      } catch (error: any) {
        setServerErrorOcurred(true);
      }
      if (contrato) {
        const content = contrato.contenido;
        const contenidoBlob = convertBase64ToBlob(
          content,
          contrato.extension || "application/pdf"
        );
        downloadBlob(contenidoBlob, contrato.nombre || "contrato.pdf");
      }
    }
  };

  const onEditarClick = () => {
    navigate(`${RUTA_PROPIETARIOS}/${idPropietario}/editar`, { replace: false });
  };

  const onAgregarMascotaClick = () => {
    navigate(`${RUTA_PROPIETARIOS}/${idPropietario}/mascotas/crear`, {
      replace: false,
    });
  };

  const onAgendamientoClick = (agendamiento: AgendamientoResumido) => {
    setAgendamientoSeleccionado(agendamiento);
  };

  const onCerrarAgendamientoModal = () => {
    setAgendamientoSeleccionado(undefined);
  };

  useEffect(() => {
    const _buscarPropietario = async (id: string) => {
      const resp = await buscarPropietario(id);
      if (resp) {
        setPropietario(resp);
      }
    };
    if (idPropietario) {
      _buscarPropietario(idPropietario);
    }
  }, [idPropietario]);

  return (
    <>
      <AgendamientoModal
        show={!!agendamientoSeleccionado}
        agendamiento={agendamientoSeleccionado}
        onClose={onCerrarAgendamientoModal}
      />
      <Row className="justify-content-center">
        <div className="col-xs-12 col-md-10 col-lg-8 col-xxl-5">
          <Card>
            <StyledCardHeader className="pt-4 pb-4">
              <IconContainer>
                <ContactPageIcon sx={{ fontSize: 100 }} />
              </IconContainer>
            </StyledCardHeader>
            <Card.Body>
              <Row className="justify-content-center mb-5">
                <Col>
                  <Row>
                    <CardTitle className="text-center">
                      {`${propietario.primerNombre} ${propietario.primerApellido} ${propietario.segundoApellido}`}
                    </CardTitle>
                  </Row>
                  <Row>
                    <ButtonsCol className="mt-4 d-flex justify-content-center">
                      <Button
                        id="buttonEditar"
                        type="button"
                        variant="primary"
                        onClick={onEditarClick}
                      >
                        <EditIcon sx={{ fontSize: 20 }} /> Editar
                      </Button>
                    </ButtonsCol>
                  </Row>

                  {!propietario.mascotas ||
                    (propietario.mascotas.length === 0 && (
                      <Row>
                        <ButtonsCol className="mt-4 d-flex justify-content-center">
                          <Button
                            id="buttonEditar"
                            type="button"
                            variant="secondary"
                            onClick={onAgregarMascotaClick}
                          >
                            <AddIcon sx={{ fontSize: 20 }} /> Agregar Mascota
                          </Button>
                        </ButtonsCol>
                      </Row>
                    ))}
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col className="col-10">
                  <TabsSection
                    mascotas={propietario.mascotas || []}
                    agendamientos={agendamientosActivos}
                    onAgregarMascotaClick={onAgregarMascotaClick}
                    onAgendamientoClick={onAgendamientoClick}
                  />

                  <h5 className="mb-4 mt-5">Identificación</h5>
                  <InfoRow
                    label="Tipo Documento"
                    value={prettyTipoDocumento(propietario.tipoDocumento)}
                  />
                  <InfoRow label="Documento" value={propietario.documento} />
                  <InfoRow
                    label="Ciudad Expedición"
                    value={propietario.ciudadExpedicion}
                  />

                  <h5 className="mb-4 mt-5">Contacto</h5>
                  <InfoRow label="Email" value={propietario.email} />
                  <InfoRow label="Celular" value={propietario.celular} />
                  <InfoRow
                    label="Nombres Contacto Alterno"
                    value={propietario.nombresContactoAlterno}
                  />
                  <InfoRow
                    label="Celular Contacto Alterno"
                    value={propietario.celularContactoAlterno}
                  />

                  <h5 className="mb-4 mt-5">Ubicación</h5>
                  <InfoRow
                    label="País Residencia"
                    value={propietario.paisResidencia?.nombre}
                  />
                  <InfoRow
                    label="Departamento Residencia"
                    value={propietario.departamentoResidencia?.nombre}
                  />
                  <InfoRow
                    label="Ciudad Residencia"
                    value={propietario.ciudadResidencia?.nombre}
                  />
                  <InfoRow
                    label="Sector Residencia"
                    value={propietario.sectorResidencia?.nombre}
                  />
                  <InfoRow
                    label="Dirección Residencia"
                    value={propietario.direccionResidencia}
                  />

                  <h5 className="mb-4 mt-5">Marketing</h5>
                  <InfoRow
                    label="Canal Marketing"
                    value={
                      propietario.canalMarketing
                        ? prettyCanalMarketing(propietario.canalMarketing)
                        : undefined
                    }
                  />
                  <InfoRow
                    label="Es Turista"
                    value={propietario.esTurista ? "Si" : "No"}
                  />
                  <InfoRow
                    label="País Proveniencia"
                    value={propietario.paisProveniencia?.nombre}
                  />
                  <InfoRow
                    label="Ciudad Proveniencia"
                    value={propietario.ciudadProveniencia?.nombre}
                  />
                  <InfoRow
                    label="Fecha Nacimiento"
                    value={propietario.fechaNacimiento}
                  />
                </Col>
              </Row>
              {propietario.tieneContrato && (
                <Row>
                  <ButtonsCol className="mt-4 d-flex justify-content-center">
                    <Button
                      id="buttonEditar"
                      type="button"
                      variant="secondary"
                      onClick={onDescargarContratoClick}
                    >
                      <DownloadIcon sx={{ fontSize: 20 }} /> Descargar Contrato
                    </Button>
                  </ButtonsCol>
                </Row>
              )}
              {(serverErrorOcurred || agendamientosIsError) && (
                <ServerErrorModal />
              )}
            </Card.Body>
          </Card>
        </div>
      </Row>
    </>
  );
}

interface InfoRowProps {
  label: string;
  value?: string;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <Row className="mb-2">
      <LabelCol className="col-6">{label + ":"}</LabelCol>
      <Col className="text-right">{value}</Col>
    </Row>
  );
}

const LabelCol = styled(Col)`
  font-weight: 500;
`;

const ButtonsCol = styled(Col)`
  gap: 1%;
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background-color: ${(props) => props.theme.colors.main};
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`;

const CardTitle = styled("h4")`
  font-weight: 500;
  color: #454545;
  text-transform: capitalize;
  letter-spacing: 0.05em;
  margin-bottom: 0px;
`;

const IconContainer = styled("span")`
  position: relative;
  margin-right: 10px;
  color: white;
  margin-left: 10px;
`;

export default CardSection;
